// src/pages/OrderPage.js
import React, { useState, useEffect } from "react";
import { useCart } from "../context/CartContext";
import { useCheckout } from "../context/CheckoutContext";
import ItemModal from "../components/ItemModal";
import Basket from "../components/Basket";
import MenuPanel from "../components/MenuPanel";
import Toast from "../components/Toast";
import { Helmet } from "react-helmet-async";
import { useApi } from "../utils/Api";
import { checkStoreStatus } from "../utils/storeHours";

const OrderPage = () => {
  const { cartItems } = useCart();
  const { openCheckout } = useCheckout();
  const { apiFetch } = useApi();

  const [selectedItem, setSelectedItem] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [openCategoryId, setOpenCategoryId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 901);
  const [toastMessage, setToastMessage] = useState("");
  const [restaurantOpen, setRestaurantOpen] = useState(true);
  const [loadingRestaurantStatus, setLoadingRestaurantStatus] = useState(true);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 901);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchRestaurantStatus = async () => {
      try {
        setLoadingRestaurantStatus(true);
        const res = await apiFetch("restaurant-status");
        if (res?.success) {
          const resIsOpen = Number(res.is_open);
          const { isTakeawayOpen } = checkStoreStatus();
          if (resIsOpen === 2) {
            setRestaurantOpen(true);
          } else if (resIsOpen === 1) {
            setRestaurantOpen(isTakeawayOpen);
          } else {
            setRestaurantOpen(false);
          }
        }
      } catch (error) {
        console.error("Failed to fetch restaurant status:", error);
      } finally {
        setLoadingRestaurantStatus(false);
      }
    };
    fetchRestaurantStatus();
  }, [apiFetch]);

  const handleCategoryClick = (id, initial = false) => {
    if (openCategoryId === id && !initial) {
      setOpenCategoryId(null);
      setActiveCategory(null);
    } else {
      setOpenCategoryId(id);
      setActiveCategory(id);
      setTimeout(() => {
        const target = document.getElementById(`category-${id}`);
        if (target) {
          const offset = 60;
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = target.getBoundingClientRect().top;
          const elementPosition = elementRect - bodyRect;
          const offsetPosition = elementPosition - offset;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        }
      }, 100);
    }
  };

  const handleAddToCart = (item) => setSelectedItem(item);
  const isInCart = (itemId) => cartItems.some(({ item }) => item.id === itemId);
  const handleCheckoutClick = (type) => openCheckout(type || null);

  return (
    <div className="order-page-container page-container">
      <Helmet>
        <title>Order Online | LasGidi Restaurant</title>
        <meta name="description" content="Order your favorite Nigerian dishes online from LasGidi Restaurant in Manchester. Enjoy quick delivery or takeaway." />
        <meta name="keywords" content="LasGidi Restaurant, Order Online, Nigerian Food Delivery, Manchester Takeaway, Food Delivery" />
        <meta property="og:title" content="Order Online | LasGidi Restaurant" />
        <meta property="og:description" content="Order your favorite Nigerian dishes online from LasGidi Restaurant in Manchester. Enjoy quick delivery or takeaway." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Left container for Menu Panel */}
      <div className="left-container">
        <MenuPanel
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          handleCheckoutClick={handleCheckoutClick}
          handleCategoryClick={handleCategoryClick}
          activeCategory={activeCategory}
          openCategoryId={openCategoryId}
          isInCart={isInCart}
          cartItems={cartItems}
          handleAddToCart={handleAddToCart}
          restaurantOpen={restaurantOpen}
          loadingRestaurantStatus={loadingRestaurantStatus}
        />
      </div>

      {/* Desktop Basket */}
      {!isMobile && (
        <div className="right-container desktop-basket">
          <Basket
            onCheckoutClick={handleCheckoutClick}
            restaurantOpen={restaurantOpen}
          />
        </div>
      )}

      {/* Mobile Basket */}
      {isMobile && (
        <Basket
          onCheckoutClick={handleCheckoutClick}
          isMobileCheckout
          restaurantOpen={restaurantOpen}
        />
      )}

      {selectedItem && (
        <ItemModal
          isOpen={!!selectedItem}
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          showToast={(msg) => setToastMessage(msg)}
          restaurantOpen={restaurantOpen}
        />
      )}

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage("")} />
      )}
    </div>
  );
};

export default OrderPage;